import {
  type UppFloorPlanElement,
  UppFloorPlanArchitecture,
  UppFloorPlanBar,
  UppFloorPlanFurniture,
  UppFloorPlanTable,
} from '../plan/floor-plan-element';
import type { UppFloorPlanTheme } from '../plan/floor-plan-theme';

/**
 * Local pixel space: origin top-left of the element box, width × height = element bbox in px.
 * Implementations add Konva nodes as children of `group` (listening false on decorative nodes).
 */
export interface FloorPlanShapePaintContext {
  readonly K: any;
  readonly group: any;
  readonly widthPx: number;
  readonly heightPx: number;
  /** Pixels per cm (room scale); used for v3-style wall thickness, bar modules, chair sizes. */
  readonly scalePxPerCm: number;
  readonly theme: UppFloorPlanTheme;
  /**
   * When true, inner geometry uses the thicker “selected” stroke (furniture family).
   * Selection highlight on the bbox is still applied by the hit-rect wrapper in `nodes/floor-plan-element-nodes`.
   */
  readonly innerStrokeUsesSelection: boolean;
  readonly element: UppFloorPlanElement;
  /**
   * English comment normalized.
   */
  readonly elementPointerListening?: boolean;
}

export type FloorPlanShapePainter = (ctx: FloorPlanShapePaintContext) => void;

export function floorPlanInnerStrokeAttrs(
  theme: UppFloorPlanTheme,
  selected: boolean,
): { stroke: string; strokeWidth: number } {
  return {
    stroke: selected ? theme.roomStroke : theme.elementStroke,
    strokeWidth: selected ? theme.roomStrokeWidth + 0.5 : theme.elementStrokeWidth,
  };
}

/** Edit-mode selection outline (same as {@link addFloorPlanHitRect} when `editSelected`). */
export function floorPlanEditSelectedStrokeAttrs(theme: UppFloorPlanTheme): {
  stroke: string;
  strokeWidth: number;
} {
  return {
    stroke: '#dc2626',
    strokeWidth: theme.roomStrokeWidth + 1.5,
  };
}

export function addFloorPlanHitRect(
  K: any,
  group: any,
  widthPx: number,
  heightPx: number,
  theme: UppFloorPlanTheme,
  selected: boolean,
  listening: boolean,
  offsetXPx = 0,
  offsetYPx = 0,
  /** Edit mode + selected: red outline (vector-edit affordance; transformer adds dashed bbox). */
  editSelected = false,
): void {
  const editSel = editSelected ? floorPlanEditSelectedStrokeAttrs(theme) : null;
  /** Selection chrome (stroke) only in EDIT (`editSelected`). In VIEW, `selected` still drives hit/emit but no ring. */
  const stroke = editSel ? editSel.stroke : 'transparent';
  const strokeWidth = editSel ? editSel.strokeWidth : 0;
  group.add(
    new K.Rect({
      x: offsetXPx,
      y: offsetYPx,
      width: widthPx,
      height: heightPx,
      fill: 'rgba(0,0,0,0.001)',
      stroke,
      strokeWidth,
      listening,
    }),
  );
}

const _painters = new Map<string, FloorPlanShapePainter>();

/**
 * Registers a painter for a stable string key (`family:variant`, e.g. `bar:LINEAR`).
 * Later registration overwrites (useful in tests only; avoid in production bundles).
 */
export function registerFloorPlanShapePainter(key: string, painter: FloorPlanShapePainter): void {
  _painters.set(key, painter);
}

export function getFloorPlanShapePainter(key: string): FloorPlanShapePainter | undefined {
  return _painters.get(key);
}

/** Konva `Rect` fallback for unknown keys (should stay rare once keys are normalized). */
export const FLOOR_PLAN_SHAPE_KEY_FALLBACK_RECT = 'fallback:rect';

const DOOR_SHAPES = new Set(['TOLEFT', 'TORIGHT']);
const WINDOW_SHAPES = new Set(['SINGLE', 'DOUBLE']);
const STAIRS_SHAPES = new Set(['SIMPLE', 'DOUBLE', 'SNAIL']);

/**
 * Maps a model element to a registry key consumed by {@link getFloorPlanShapePainter}.
 * To add a new drawable family, extend this function and register a painter (see `shapes/README.md`).
 */
export function resolveFloorPlanShapePaintKey(el: UppFloorPlanElement): string {
  if (el instanceof UppFloorPlanBar) {
    return `bar:${el.barShape}`;
  }
  if (el instanceof UppFloorPlanArchitecture) {
    const c = el.architectureClass;
    const s = el.architectureShape;
    if (c === 'door') {
      const swing = DOOR_SHAPES.has(s) ? s : 'TOLEFT';
      return `door:${swing}`;
    }
    if (c === 'window') {
      const pane = WINDOW_SHAPES.has(s) ? s : 'SINGLE';
      return `window:${pane}`;
    }
    const flight = STAIRS_SHAPES.has(s) ? s : 'SIMPLE';
    return `stairs:${flight}`;
  }
  if (el instanceof UppFloorPlanFurniture) {
    if (el.furnitureClass === 'generic') {
      return `furniture:generic:${el.genericShape}`;
    }
    return `furniture:${el.furnitureClass}`;
  }
  if (el instanceof UppFloorPlanTable) {
    return el.tableShape === 'ELLIPSE' ? 'table:ELLIPSE' : 'table:RECT';
  }
  return FLOOR_PLAN_SHAPE_KEY_FALLBACK_RECT;
}
